import { retrieveLaunchParams } from '@telegram-apps/sdk-react';

/**
 * Возраст пропуска Telegram (`initData`) глазами клиента — work-038.
 *
 * Telegram выдаёт `initData` ОДИН раз при запуске приложения; метода «обновить»
 * не существует. Сервер отвергает пропуск старше `INITDATA_MAX_AGE_SEC`
 * (`apps/api/src/env.ts:92`, код-дефолт 86400 с = 24 ч) и отвечает
 * `401 { error:'unauthorized', code:'stale' }`. Этот модуль даёт UI возможность
 * ПРЕДУПРЕДИТЬ человека заранее и объяснить причину отказа честно.
 *
 * Клиент читает поле распарсенного initData ради собственного UI — прецедент
 * `isPremiumUser()` (`lib/tg.ts:83`). CANON D1/SP3 это не нарушает: сервер
 * по-прежнему единственный, кто валидирует подпись и возраст; клиентская оценка —
 * только подсказка и НИКОГДА не гейт.
 *
 * Часы устройства могут врать (сбитая дата, ручной перевод времени), поэтому
 * возраст проходит санитарную проверку правдоподобия: отрицательный или больше
 * 48 ч — значит часам не верим и молчим (`undefined`), а не пугаем человека.
 *
 * CANON G3: ни одна функция никогда не бросает — вне Telegram / при ошибке SDK
 * возвращается `undefined`.
 */

/**
 * Порог раннего предупреждения — 23 ч.
 *
 * На час меньше серверного код-дефолта (86400 с), чтобы человек успел
 * перезапустить приложение до отказа, а не узнал о протухшем пропуске
 * в момент отправки текста.
 *
 * ⚠️ ЖЁСТКАЯ СВЯЗЬ С СЕРВЕРОМ, которую код не может проверить сам. Значение
 * прибито к код-дефолту `INITDATA_MAX_AGE_SEC` = 86400 (`apps/api/src/env.ts`),
 * но живёт в другом приложении и вычислить его из серверного не может: сервер
 * своё окно наружу не отдаёт. Если владелец задаст на проде более короткое
 * окно (например 3600), предупреждение станет бесполезным — оно сработает
 * через 23 часа после того, как сервер уже 22 часа как отказывает.
 * Меняешь `INITDATA_MAX_AGE_SEC` на проде — правь и это число.
 * Связь продублирована в `.claude/skills/deploy/SKILL.md` и ADR 0016.
 */
export const INITDATA_WARN_AGE_SEC = 82_800;

/**
 * Потолок правдоподобия — 48 ч. Больше этого возраст быть не может даже
 * при самом длинном разумном сеансе: значит врут часы устройства.
 */
const MAX_PLAUSIBLE_AGE_SEC = 172_800;

/**
 * Момент выдачи пропуска (unix-секунды) или `undefined`.
 *
 * SDK 3.x отдаёт `tgWebAppData.auth_date` объектом `Date`, а не числом —
 * сверено по типам установленного пакета:
 * `@telegram-apps/types/dist/dts/init-data.d.ts:84` (`auth_date: Date`) и
 * `@telegram-apps/transformers/dist/dts/parsers/parseLaunchParamsQuery.d.ts:4`.
 * Числовую форму тоже принимаем — на случай смены транспорта в SDK и ради
 * тестовых моков; неизвестная форма и невалидная дата дают `undefined`.
 */
export function getAuthDateSec(): number | undefined {
  try {
    const lp = retrieveLaunchParams();
    // Тип поля — Date (см. докстринг выше), но читаем как unknown:
    // рантайм важнее объявленного типа, форма может измениться.
    const raw: unknown = lp.tgWebAppData?.auth_date;
    if (raw instanceof Date) {
      const sec = Math.floor(raw.getTime() / 1000);
      return Number.isFinite(sec) ? sec : undefined;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    return undefined;
  } catch {
    // Вне Telegram / launch params недоступны — возраст неизвестен (CANON G3).
    return undefined;
  }
}

/**
 * Возраст пропуска в секундах или `undefined`, если он неизвестен либо
 * неправдоподобен (часы устройства врут).
 *
 * `undefined` при: недоступном `auth_date`, отрицательном возрасте (дата выдачи
 * в будущем) и возрасте больше 48 ч (`MAX_PLAUSIBLE_AGE_SEC`).
 */
export function getInitDataAgeSec(): number | undefined {
  const authDateSec = getAuthDateSec();
  if (authDateSec === undefined) return undefined;
  const ageSec = Math.floor(Date.now() / 1000) - authDateSec;
  if (ageSec < 0 || ageSec > MAX_PLAUSIBLE_AGE_SEC) return undefined;
  return ageSec;
}

/**
 * Пора ли предупредить о скором истечении пропуска.
 *
 * Чистая функция (вынесена отдельно ради теста `SessionAgeWarning` без SDK):
 * неизвестный возраст — молчим.
 */
export function shouldWarnAge(ageSec: number | undefined): boolean {
  return ageSec !== undefined && ageSec >= INITDATA_WARN_AGE_SEC;
}
